import React from 'react';
import {useParams} from 'react-router-dom';
import {gql, useQuery} from '@apollo/client';
import {Box, Typography} from '@mui/material';
import {Email} from '@mui/icons-material';
import {ErrorPage} from '../ErrorPage';
import {StaffSearchWrapped} from './StaffSearchPage';
import {STAFF_DETAILS} from '../../graphql/fragments';
import Loading from '../../components/Loading/Loading';
import {GetStaff, GetStaffVariables} from '../../schema-types/GetStaff';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {formatGroupName} from '../PermissionsAuditPage/useAllStaff';
import {AdminDetailPanelContent} from '../PermissionsAuditPage/PermissionsDetailPanelContent';
import {HistoryQueryType} from '../../components/AccountReviewDialog/useHistoryDiff';
import {HistoryDiff} from '../../components/AccountReviewDialog/HistoryDiffDialog';
import {PermissionFormWrapped} from '../../components/PermissionDialog/PermissionDialog';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {OpenInCuro} from '../../utils/getCuroURL';

export const GET_STAFF_QUERY = gql`
  query GetStaff($staffId: UUID!) {
    staff(staffId: $staffId) {
      ...StaffDetails
    }
  }
  ${STAFF_DETAILS}
`;

export const StaffPage: React.FC = () => {
  const {staffId} = useParams<{staffId: string}>();
  if (!staffId) {
    return (
      <>
        <StaffSearchWrapped formFields={[]} />
        <ErrorPage text="Staff member doesn't exist" />
      </>
    );
  }
  return (
    <>
      <StaffSearchWrapped formFields={[]} />
      <StaffPageInner staffId={staffId} />
    </>
  );
};

export const StaffPageInner: React.FC<{staffId: string}> = ({staffId}) => {
  const {data, loading} = useQuery<GetStaff, GetStaffVariables>(GET_STAFF_QUERY, {
    variables: {
      staffId,
    },
  });
  if (loading) {
    return <Loading />;
  }
  if (!data?.staff?.[0]) {
    return <ErrorPage text="Staff doesn't exist" />;
  }
  const staff = data.staff[0];

  return (
    <>
      <Box mt={7} />
      <Panel title="Staff details" color={themeColors.chartPurple}>
        <Box p={2} pt={1}>
          <Typography variant="h3">{staff.name}</Typography>
          <Typography variant="h5">
            {staff.businessUnit?.name} ({staff.businessUnit?.partnershipType})
          </Typography>
          <Box display="flex" alignContent="center">
            <Email fontSize="small" />
            <Typography pl={1} variant="body1">
              {staff.email}
            </Typography>
          </Box>
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_curo_FE]}
            showErrorIfDenied={false}
          >
            <Typography variant="h5">Curo id: {staff.curoId}</Typography>
            <Box mt={1}>
              <OpenInCuro
                curoType="systemuser"
                curoId={staff.curoId}
                alwaysShow
                variant="outlined"
                color="primary"
              >
                Open in CURO
              </OpenInCuro>
            </Box>
          </FeatureBoundary>
        </Box>
      </Panel>
      <Box mt={7} />
      <Panel title="Feature Permissions" color={themeColors.coral}>
        <Box p={2}>
          <Typography variant="h5">
            User group: {staff.groups?.map((x) => formatGroupName(x?.name)).join('')}
          </Typography>
        </Box>
        <AdminDetailPanelContent staff={staff} />
      </Panel>
      <Box mt={7} />
      <Panel title="Third Party Permissions" color={themeColors.blue}>
        <Box p={2}>
          <PermissionFormWrapped formFields={[]} selectedStaff={staff} />
        </Box>
      </Panel>
      <Box mt={7} />
      <Panel title="Audit History" color={themeColors.green}>
        <Box p={2}>
          <HistoryDiff hideRecord staffId={staff.id} queryType={HistoryQueryType.STAFF} />
        </Box>
      </Panel>
      <Box mt={7} />
    </>
  );
};
